import { memo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { CheckCircleFilled, RiseOutlined } from '@ant-design/icons';

import {
    type ReferralProgramInfo,
    useEnrollReferralMutation,
} from '@/entities/affiliate';

import { useCountUp } from '@/shared/lib/hooks';
import { logError } from '@/shared/lib/logging';
import { Button } from '@/shared/ui/button';

import dashboardPreviewSrc from '@/shared/assets/images/affiliate/affiliate-dashboard-preview.png';

import cls from './affiliate-join.module.css';

interface AffiliateJoinProps {
    program?: ReferralProgramInfo | undefined;
}

export const AffiliateJoin = memo((props: AffiliateJoinProps) => {
    const { program } = props;

    const { t } = useTranslation('affiliate');

    const [enroll, { isLoading: isEnrolling }] = useEnrollReferralMutation();

    const panelRef = useRef<HTMLDivElement>(null);

    const commissionPct = Math.round(
        parseFloat(program?.commission_value ?? '10'),
    );

    const animatedPct = useCountUp(commissionPct, 900);

    useEffect(() => {
        const el = panelRef.current;

        if (!el) {
            return undefined;
        }

        const canTilt =
            window.matchMedia('(hover: hover)').matches &&
            !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (!canTilt) {
            return undefined;
        }

        const handleMove = (event: MouseEvent) => {
            const rect = el.getBoundingClientRect();
            const x = (event.clientX - rect.left) / rect.width - 0.5;
            const y = (event.clientY - rect.top) / rect.height - 0.5;

            el.style.transform = `perspective(1400px) rotateY(${x * 6}deg) rotateX(${y * -4}deg)`;
        };

        const handleLeave = () => {
            el.style.transform =
                'perspective(1400px) rotateY(0deg) rotateX(0deg)';
        };

        el.addEventListener('mousemove', handleMove);
        el.addEventListener('mouseleave', handleLeave);

        return () => {
            el.removeEventListener('mousemove', handleMove);
            el.removeEventListener('mouseleave', handleLeave);
        };
    }, []);

    const steps = [
        {
            title: t('join.step1Title'),
            text: t('join.step1Text'),
        },
        {
            title: t('join.step2Title'),
            text: t('join.step2Text'),
        },
        {
            title: t('join.step3Title'),
            text: t('join.step3Text'),
        },
    ];

    const handleEnroll = async () => {
        try {
            await enroll().unwrap();
        } catch (error) {
            logError('Failed to enroll into referral program:', error);
        }
    };

    return (
        <div className={cls.joinContainer}>
            <section className={cls.hero}>
                <div className={cls.heroCopy}>
                    <span className={cls.eyebrow}>{t('join.eyebrow')}</span>
                    <h1 className={cls.headline}>
                        {t('join.headline')}{' '}
                        <span className={cls.headlineAccent}>
                            {t('join.headlineAccent')}
                        </span>
                    </h1>
                    <p className={cls.subtitle}>{t('join.subtitle')}</p>
                    <div className={cls.ctaRow}>
                        <Button
                            isLoading={isEnrolling}
                            onClick={handleEnroll}
                            title={t('join.cta')}
                            variant="primary"
                        />
                        <span className={cls.ctaHint}>{t('join.ctaHint')}</span>
                    </div>
                    <div className={cls.heroStats}>
                        <div className={cls.heroStat}>
                            <span className={cls.heroStatValue}>
                                {animatedPct}%
                            </span>
                            <span className={cls.heroStatLabel}>
                                {t('join.pills.commissionLabel')}
                            </span>
                        </div>
                        <div className={cls.heroStat}>
                            <span className={cls.heroStatValue}>
                                {t('join.pills.recurringValue')}
                            </span>
                            <span className={cls.heroStatLabel}>
                                {t('join.pills.recurringLabel')}
                            </span>
                        </div>
                        <div className={cls.heroStat}>
                            <span className={cls.heroStatValue}>
                                {t('join.pills.cookieValue')}
                            </span>
                            <span className={cls.heroStatLabel}>
                                {t('join.pills.cookieLabel')}
                            </span>
                        </div>
                    </div>
                </div>

                <div className={cls.heroVisual}>
                    <div className={cls.heroGlow} />
                    <div
                        ref={panelRef}
                        className={cls.heroPanel}
                    >
                        <div className={cls.heroFrame}>
                            <img
                                alt={t('join.panelAlt')}
                                className={cls.heroShot}
                                src={dashboardPreviewSrc}
                            />
                        </div>
                        <div className={cls.floatCardBalance}>
                            <span className={cls.floatLabel}>
                                {t('join.floatBalanceLabel')}
                            </span>
                            <div className={cls.floatRow}>
                                <span className={cls.floatValue}>
                                    {t('join.floatBalanceValue')}
                                </span>
                                <span className={cls.floatBadge}>
                                    <RiseOutlined />
                                    {t('join.floatBalanceBadge')}
                                </span>
                            </div>
                            <svg
                                className={cls.sparkline}
                                fill="none"
                                viewBox="0 0 120 32"
                            >
                                <path
                                    d="M2 27 L18 23 L34 25 L50 17 L66 19 L82 11 L98 13 L118 4"
                                    stroke="currentColor"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2.5"
                                />
                            </svg>
                        </div>
                        <div className={cls.floatCardConversion}>
                            <span className={cls.floatIcon}>
                                <CheckCircleFilled />
                            </span>
                            <div className={cls.floatConversionBody}>
                                <span className={cls.floatConversionTitle}>
                                    {t('join.floatConversionTitle')}
                                </span>
                                <span className={cls.floatConversionAmount}>
                                    {t('join.floatConversionAmount')}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className={cls.howSection}>
                <span className={cls.howTitle}>{t('join.howTitle')}</span>
                <ol className={cls.steps}>
                    {steps.map((step, index) => (
                        <li
                            key={step.title}
                            className={cls.step}
                        >
                            <span className={cls.stepNumber}>{index + 1}</span>
                            <div className={cls.stepBody}>
                                <span className={cls.stepTitle}>
                                    {step.title}
                                </span>
                                <span className={cls.stepText}>
                                    {step.text}
                                </span>
                            </div>
                        </li>
                    ))}
                </ol>
            </section>

            <div className={cls.ctaWrap}>
                <Button
                    isLoading={isEnrolling}
                    onClick={handleEnroll}
                    title={t('join.cta')}
                    variant="primary"
                />
                <span className={cls.ctaHint}>{t('join.ctaHint')}</span>
            </div>
        </div>
    );
});

AffiliateJoin.displayName = 'AffiliateJoin';
